import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  LinearProgress,
  Tooltip,
  IconButton,
  Badge,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import correlationService, { ComprehensiveCorrelationResponse } from '../../features/cross-document/correlationService';

interface CorrelationValidatorProps {
  documentSetId: string;
  documentCount: number;
  declarationType?: 'export' | 'import' | 'transit';
  onValidationComplete?: (result: ComprehensiveCorrelationResponse) => void;
  onValidationError?: (error: string) => void;
  autoValidate?: boolean;
}

export const CorrelationValidator: React.FC<CorrelationValidatorProps> = ({
  documentSetId,
  documentCount,
  declarationType = 'export',
  onValidationComplete,
  onValidationError,
  autoValidate = false,
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<ComprehensiveCorrelationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleValidate = async () => {
    if (documentCount < 2) {
      const errorMsg = 'Please upload at least 2 documents before validating correlation';
      setError(errorMsg);
      if (onValidationError) {
        onValidationError(errorMsg);
      }
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const result = await correlationService.validateCorrelation(
        documentSetId,
        declarationType
      );

      setValidationResult(result);

      if (onValidationComplete) {
        onValidationComplete(result);
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to validate document correlation';
      setError(errorMessage);

      if (onValidationError) {
        onValidationError(errorMessage);
      }
    } finally {
      setIsValidating(false);
    }
  };

  React.useEffect(() => {
    if (autoValidate && documentCount >= 2 && !validationResult && !isValidating) {
      handleValidate();
    }
  }, [autoValidate, documentCount]);

  // Defensive access to nested response fields — partial/malformed responses
  // must not crash the render (there is no error boundary around this card)
  const correlationStatus = validationResult?.correlation_details?.correlation_status;
  const canSubmit = validationResult?.atlas_validation?.can_submit ?? false;
  const correlatedGroups = validationResult?.correlated_groups ?? [];
  const uncorrelatedDocuments = validationResult?.uncorrelated_documents ?? [];
  const atlasIssues = validationResult?.atlas_validation?.issues ?? [];
  const atlasRecommendations = validationResult?.atlas_validation?.recommendations ?? [];

  const getStatusIcon = () => {
    if (!validationResult) return null;

    if (validationResult.correlation_valid && canSubmit) {
      return <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 40 }} />;
    } else if (correlationStatus === 'partially_correlated') {
      return <WarningIcon sx={{ color: '#FF9800', fontSize: 40 }} />;
    } else {
      return <ErrorIcon sx={{ color: '#F44336', fontSize: 40 }} />;
    }
  };

  const getStatusMessage = () => {
    if (!validationResult) return '';

    if (validationResult.correlation_valid && canSubmit) {
      return 'Documents are properly correlated and ready for ATLAS submission';
    } else if (correlationStatus === 'partially_correlated') {
      return 'Some documents are correlated, but issues found';
    } else {
      return 'Documents are not properly correlated';
    }
  };

  const confidenceLabel = validationResult
    ? correlationService.getConfidenceLabel(validationResult.confidence ?? 0)
    : null;

  return (
    <Card elevation={3} sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            <Typography variant="h6" component="div">
              Document Correlation Validation
            </Typography>
            <Tooltip title="Validates that all uploaded documents are properly related and meet ATLAS requirements">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {validationResult && (
              <Chip
                label={`${documentCount} Documents`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={handleValidate}
              disabled={isValidating || documentCount < 2}
              startIcon={isValidating ? <CircularProgress size={20} /> : <RefreshIcon />}
            >
              {isValidating ? 'Validating...' : validationResult ? 'Re-validate' : 'Validate Correlation'}
            </Button>
          </Box>
        </Box>

        {/* Document count warning */}
        {documentCount < 2 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Upload at least 2 documents to validate correlation
          </Alert>
        )}

        {/* Validation in progress */}
        {isValidating && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Analyzing document relationships and validating ATLAS requirements...
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Validation results summary */}
        {validationResult && !isValidating && (
          <Box sx={{ mt: 2 }}>
            {/* Status indicator */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: validationResult.correlation_valid ? '#E8F5E9' : '#FFF3E0',
              }}
            >
              <Box>{getStatusIcon()}</Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {getStatusMessage()}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  {/* Confidence score */}
                  {confidenceLabel && (
                    <Chip
                      label={`Confidence: ${correlationService.toPercentage(validationResult.confidence ?? 0)}%`}
                      color={confidenceLabel.severity}
                      size="small"
                    />
                  )}

                  {/* Correlation status */}
                  <Chip
                    label={(correlationStatus ?? 'not_correlated').replace('_', ' ').toUpperCase()}
                    color={
                      correlationStatus === 'fully_correlated'
                        ? 'success'
                        : correlationStatus === 'partially_correlated'
                        ? 'warning'
                        : 'error'
                    }
                    size="small"
                  />

                  {/* ATLAS ready status */}
                  <Chip
                    label={canSubmit ? 'ATLAS Ready' : 'ATLAS Not Ready'}
                    color={canSubmit ? 'success' : 'error'}
                    size="small"
                    icon={canSubmit ? <CheckCircleIcon /> : <ErrorIcon />}
                  />

                  {/* Correlated pairs */}
                  <Chip
                    label={`${validationResult.correlation_details?.correlated_pairs_count ?? 0}/${validationResult.correlation_details?.total_pairs_count ?? 0} Pairs Correlated`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Box>

            {/* Quick stats */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mt: 2 }}>
              <Box sx={{ p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Correlated Groups
                </Typography>
                <Typography variant="h6">
                  {correlatedGroups.length}
                </Typography>
              </Box>

              <Box sx={{ p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Uncorrelated Documents
                </Typography>
                <Typography variant="h6" color={uncorrelatedDocuments.length > 0 ? 'error' : 'success.main'}>
                  {uncorrelatedDocuments.length}
                </Typography>
              </Box>

              <Box sx={{ p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  ATLAS Issues
                </Typography>
                <Typography variant="h6" color={atlasIssues.length > 0 ? 'error' : 'success.main'}>
                  {atlasIssues.length}
                </Typography>
              </Box>

              <Box sx={{ p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Recommendations
                </Typography>
                <Typography variant="h6" color="info.main">
                  {atlasRecommendations.length}
                </Typography>
              </Box>
            </Box>

            {/* Issues summary */}
            {atlasIssues.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {atlasIssues.length} Issue
                  {atlasIssues.length > 1 ? 's' : ''} Found:
                </Typography>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {atlasIssues.slice(0, 3).map((issue, index) => (
                    <li key={index}>
                      <Typography variant="body2">{issue}</Typography>
                    </li>
                  ))}
                  {atlasIssues.length > 3 && (
                    <li>
                      <Typography variant="body2">
                        +{atlasIssues.length - 3} more issues
                      </Typography>
                    </li>
                  )}
                </ul>
              </Alert>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CorrelationValidator;
